import { inject, Pipe, PipeTransform } from '@angular/core';
import { CourseDuration, OptionalPrice } from '@lx/shared/data-access/types';
import { DurationUnitsService as EslDurationService } from '../../shared/services/duration-units.service';
import { DurationUnitsService } from '@lx/shared/utils';
import { valueIsNullOrUndefined } from '@lx/shared/utils';

/**
 * Formats optional price duration information for display.
 * Displays quantity ranges with or without duration units (e.g., "(1-10 weeks)", "(5+)").
 *
 * @example
 * {{ optional | optionalPriceDuration }} // "(1-10 weeks)" or "(5+ lessons)" or "(1-10)"
 */
@Pipe({
  standalone: true,
  name: 'lxOptionalPriceDuration',
})
export class OptionalPriceDurationPipe implements PipeTransform {
  private readonly eslDurationService = inject(EslDurationService);
  private readonly durationService = inject(DurationUnitsService);

  transform(optionalPrice: OptionalPrice | null | undefined): string {
    if (!optionalPrice) {
      return '';
    }

    const { durationUnit, minQuantity, maxQuantity } = optionalPrice;

    // normalize presence checks once to avoid duplicated conditions
    const minQuantityPresent = !valueIsNullOrUndefined(minQuantity);
    const maxQuantityPresent = !valueIsNullOrUndefined(maxQuantity);
    const bothQuantitiesPresent = minQuantityPresent && maxQuantityPresent;

    // If both quantities are missing, we can't render a meaningful duration
    if (!minQuantityPresent && !maxQuantityPresent) {
      return '';
    }

    if (!durationUnit) {
      if (bothQuantitiesPresent) {
        return `(${minQuantity}-${maxQuantity})`;
      }
      if (minQuantityPresent) {
        return `(${minQuantity}+)`;
      }
      // only max available
      return `(${maxQuantity})`;
    }

    if (bothQuantitiesPresent) {
      const courseDuration: CourseDuration = {
        durationMin: minQuantity,
        durationMax: maxQuantity,
        durationUnit,
      };
      const durationRange = this.durationService.getDurationRange(courseDuration);
      return `(${durationRange})`;
    }

    if (minQuantityPresent) {
      const durationPluralText = this.eslDurationService.getDurationPluralText(durationUnit);
      return `(${minQuantity}+ ${durationPluralText})`;
    }

    return '';
  }
}
